import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import moment from "moment-timezone";
import { ReactNode, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SidesheetType,
  closeSidesheet,
  selectCurrentSidesheet,
  showAddToCalendar,
  setNewSessionPlaceholder,
} from "store/calendarSidebarSlice";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import { useCalendarSidebarContext } from "../../../../../calendar-sidebar/context/CalendarSidebarContext";
import { useCalendarFreeze } from "../../../../contexts/calendar-freeze-context";
import { useSessionTypes } from "contexts/SessionTypesContext";
import type { TScheduleIntervalTimeSlot } from "../../types";
import { WrappedCalendarEventWithColumnPosition } from "../../utils/splitEventsIntoColumns";
import AppointmentTimeslot from "../appointment-timeslot/AppointmentTimeslot";
import BlockedIntervalTimeslot from "../blocked-interval-timeslot/BlockedIntervalTimeslot";
import GCalEventTimeslot from "../gcal-event-timeslot/GCalEventTimeslot";
import GroupSessionTimeslot from "../group-session-timeslot/GroupSessionTimeslot";
import PlaceholderTimeslot from "../placeholder-timeslot/PlaceholderTimeslot";
import ScheduleIntervalTimeslot from "../schedule-interval-timeslot/ScheduleIntervalTimeslot";
import styles from "./TimeEntry.module.scss";
import CalendarDropZone from "./components/CalendarDropZone";

type Props = {
  isToday: boolean;
  hour: number;
  scheduleIntervals: TScheduleIntervalTimeSlot[];
  placeholders: SessionPlaceholder[] | undefined;
  eventsWithPosition?: WrappedCalendarEventWithColumnPosition[];
  date: Date;
  children: ReactNode;
  className?: string;
  amplitudePageName: AmplitudePageName;
  teamMemberId?: string;
};
const TimeEntry = ({
  isToday,
  hour,
  date,
  eventsWithPosition = [],
  scheduleIntervals = [],
  placeholders = [],
  children,
  className,
  amplitudePageName,
  teamMemberId,
}: Props) => {
  const dispatch = useDispatch();
  const currentSidesheet = useSelector(selectCurrentSidesheet);

  const { currentCalendarTimezone } = useCalendarTime();
  const { isFreezed } = useCalendarFreeze();
  const { setProviderId } = useSessionTypes();

  const startValue = useMemo(() => {
    const start = moment(+date).tz(currentCalendarTimezone);
    start.hours(hour);
    return +start;
  }, [currentCalendarTimezone, date, hour]);

  const { requestCancelAddNewSession } = useCalendarSidebarContext();

  const createSessionOrCloseSidesheet = useCallback(
    (offsetMins: number) => {
      // Set provider when clicking on a provider's cell
      if (teamMemberId) {
        setProviderId(teamMemberId);
      }

      if (
        currentSidesheet === SidesheetType.AddToCalendar &&
        requestCancelAddNewSession
      ) {
        requestCancelAddNewSession();
      } else if (currentSidesheet !== SidesheetType.None) {
        dispatch(closeSidesheet());
      } else if (!isFreezed) {
        track(amplitudePageName, ["calendar", "new-session"], "click");

        // Reduce precision from 15 to 30 minutes
        const adjustedOffsetMins = offsetMins - (offsetMins % 30);

        const defaultStartTime = startValue + adjustedOffsetMins * 60 * 1000;

        dispatch(
          showAddToCalendar({
            hasInitialTime: true,
            defaultStartTime,
          }),
        );

        // Create placeholder with optional team member ID
        dispatch(
          setNewSessionPlaceholder({
            appointment_start: defaultStartTime.toString(),
            appointment_end: (defaultStartTime + 30 * 60 * 1000).toString(), // 30 minutes
            team_member_id: teamMemberId,
          }),
        );
      }
    },
    [
      currentSidesheet,
      dispatch,
      isFreezed,
      requestCancelAddNewSession,
      startValue,
      amplitudePageName,
      teamMemberId,
      setProviderId,
    ],
  );

  return (
    <td
      rowSpan={1}
      className={getClasses(
        className,
        styles["time-entry"],
        !isToday &&
          scheduleIntervals.every((el) => (el?.duration || [])[1] !== 60) &&
          styles["time-entry--no-schedule"],
      )}
    >
      {/* time line */}
      {children}
      <div
        className={getClasses(
          styles["time-entry__content"],
          isToday && styles["time-entry__content--active"],
        )}
      >
        {scheduleIntervals.map((entry, i) =>
          !entry ? null : (
            <ScheduleIntervalTimeslot
              key={i}
              scheduleInterval={entry}
              date={date}
              isToday={isToday}
            />
          ),
        )}
        {eventsWithPosition.map((wrappedEvent) => {
          switch (wrappedEvent.type) {
            case "appointment":
              return (
                <AppointmentTimeslot
                  key={wrappedEvent.id}
                  appointment={wrappedEvent.event}
                  amplitudePageName={amplitudePageName}
                  date={date}
                  calendarColumnPosition={wrappedEvent.calendarColumnPosition}
                />
              );
            case "blocked-interval":
              return (
                <BlockedIntervalTimeslot
                  key={wrappedEvent.id}
                  blockedInterval={wrappedEvent.event}
                  date={date}
                  calendarColumnPosition={wrappedEvent.calendarColumnPosition}
                />
              );
            case "gcal-event":
              return (
                <GCalEventTimeslot
                  key={wrappedEvent.id}
                  value={wrappedEvent.event}
                  date={date}
                  calendarColumnPosition={wrappedEvent.calendarColumnPosition}
                />
              );
            case "group-session":
              return (
                <GroupSessionTimeslot
                  key={wrappedEvent.id}
                  groupSession={wrappedEvent.event}
                  date={date}
                  calendarColumnPosition={wrappedEvent.calendarColumnPosition}
                />
              );
            default:
              return null;
          }
        })}
        {placeholders.map((placeholder) => (
          <PlaceholderTimeslot
            key={placeholder.appointment_start}
            placeholder={placeholder}
            date={date}
          />
        ))}
        <div className={styles["time-entry__drop-zone-container"]}>
          {[0, 15, 30, 45].map((offsetMins) => (
            <CalendarDropZone
              key={`drop-zone-${startValue}+${offsetMins}`}
              startValue={startValue + offsetMins * 60 * 1000}
              amplitudePageName={amplitudePageName}
              onClick={() => createSessionOrCloseSidesheet(offsetMins)}
            />
          ))}
        </div>
      </div>
    </td>
  );
};

export default TimeEntry;
